import * as React from 'react';
import { graphql } from 'react-apollo';

import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { groupBy } from 'lodash';
import PropTypes from 'prop-types';
import Q from 'q';
import { compose, withProps } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import redirect from 'js/lib/coursera.redirect';
import deferToClientSideRender from 'js/lib/deferToClientSideRender';

import { Button, Grid } from '@coursera/cds-core';
import { StyleSheet, css } from '@coursera/coursera-ui';
import { API_BEFORE_SEND, API_IN_PROGRESS, API_SUCCESS } from '@coursera/coursera-ui/lib/constants/sharedConstants';
import type {
  FindByProgramIdsOrContractIdsOrBothAndIncludeDeletedRequest,
  FindByProgramIdsOrContractIdsOrBothAndIncludeDeletedResponse,
} from '@coursera/grpc-types-enterpriseuser/coursera/proto/enterpriseuser/rosters/v1/rosters_api';

import type { PropsFromWithApiHandler } from 'bundles/coursera-ui/components/hocs/withApiHandler';
import withApiHandler from 'bundles/coursera-ui/components/hocs/withApiHandler';
import enterpriseAdminUser from 'bundles/enterprise-admin';
import { PREFIX, RESEND_INVITATIONS } from 'bundles/enterprise-admin-constants/ProgramInvitationsApiTagConstants';
import { COURSERA_FOR_CAMPUS_BASIC_SETUP_PATH } from 'bundles/enterprise-admin-coursera-for-campus-basic/constants';
import { GWG_CONTINUE_PROGRAM_SETUP_PATH } from 'bundles/enterprise-admin-grow-with-google/components/withGrowWithGoogleSetup';
import ProgramCard from 'bundles/enterprise-admin-home/components/ProgramCard';
import ProgramSetupCard from 'bundles/enterprise-admin-home/components/ProgramSetupCard';
import ProgramSummaryContractsModal from 'bundles/enterprise-admin-home/components/ProgramSummaryContractsModal';
import ProgramSummaryListDetailsGraphql from 'bundles/enterprise-admin-home/queries/ProgramSummaryListDetails.graphql';
import type {
  ProgramSummaryListDetailsQuery,
  ProgramSummaryListDetailsQueryVariables,
} from 'bundles/enterprise-admin-home/queries/__generated__/ProgramSummaryListDetails';
import type { GroupedEnrollmentCaps, GroupedProgramSummaries } from 'bundles/enterprise-admin-home/types';
import { resendAllInvitations } from 'bundles/enterprise-admin-home/utils/ProgramInvitationAPIUtils';
import {
  getAssignContentLink,
  getManageProgramLearnersLink,
  getProgramMainLink,
  getSkillSetProgramMainLink,
} from 'bundles/enterprise-admin-navigation';
import EnterprisePermissionsRequired from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsRequired';
import {
  ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN,
  ENTERPRISE_ADMIN_PERMISSION_VERB,
} from 'bundles/enterprise-admin-roles/types';
import { BulkEmailConfirm } from 'bundles/enterprise-ui';
import type { InjectedNaptime } from 'bundles/naptimejs';
import Naptime from 'bundles/naptimejs';
import EnterpriseProgramSummaries from 'bundles/naptimejs/resources/enterpriseProgramSummaries.v2';
import type EnterpriseProgramsV1 from 'bundles/naptimejs/resources/enterprisePrograms.v1';
import { TrackedReactLink } from 'bundles/page/components/TrackedLink2';
import Modal from 'bundles/phoenix/components/Modal';

import _t from 'i18n!nls/enterprise-admin-home';

const styles = StyleSheet.create({
  ProgramCard: {
    marginBottom: '36px',
  },
  ProgramCardLink: {
    textDecoration: 'none !important',
  },
});

type PropsFromCaller = {
  programs: Array<EnterpriseProgramsV1>;
  showProgramIds: boolean;
  thirdPartyOrganization: { id: string; slug: string };
  groupedEnrollmentCaps?: GroupedEnrollmentCaps;
  gwgProgramIds?: Array<string>;
};

type PropsFromNaptime = {
  naptime: InjectedNaptime;
  programSummaries: EnterpriseProgramSummaries[];
};

type PropsFromAdminHomeEnterpriseUserRosterQuery = {
  rosterRecords?: FindByProgramIdsOrContractIdsOrBothAndIncludeDeletedResponse['rosterRecords'];
};

type PropsFromGraphql = PropsFromAdminHomeEnterpriseUserRosterQuery;

type PropsFromWithProps = {
  groupedProgramSummaries?: GroupedProgramSummaries;
  enableGwGFreemiumMerge: boolean;
  enableGwGPaidMerge: boolean;
};

type PropsFromWithStateForProgramIdForManagingContracts = {
  programIdForManagingContracts?: string;
  setProgramIdForManagingContracts: (id?: string) => void;
};

type PropsFromHooks = {
  enterpriseProgramMetrics: ProgramSummaryListDetailsQuery['EnterpriseAdminProgramMetrics'];
  showConfirmationModal: boolean;
  showSentModal: boolean;
  setShowConfirmationModal: (x0: boolean) => void;
  setShowSentModal: (x0: boolean) => void;
  resendEmailProgramId: string;
  setResendEmailProgramId: (x0: string) => void;
} & PropsFromWithStateForProgramIdForManagingContracts;

export type Props = PropsFromCaller &
  PropsFromWithApiHandler &
  PropsFromNaptime &
  PropsFromGraphql &
  PropsFromWithProps &
  PropsFromHooks;

export class ProgramSummaryList extends React.Component<Props> {
  static contextTypes = {
    enableSkillsPlanningForOrg: PropTypes.bool,
    router: PropTypes.object.isRequired,
    enableNewProgramCreation: PropTypes.bool,
  };

  declare context: LegacyContextType<typeof ProgramSummaryList.contextTypes>;

  componentDidUpdate({ apiStatus }: Props) {
    const { apiStatus: currentApiStatus, setShowSentModal, setShowConfirmationModal } = this.props;
    if (apiStatus === API_SUCCESS && currentApiStatus === API_BEFORE_SEND) {
      setShowConfirmationModal(false);
      setShowSentModal(true);
    }
  }

  handleConfirmationModalCancel = () => {
    const { setShowConfirmationModal } = this.props;
    setShowConfirmationModal(false);
  };

  handleDismissSentModal = () => {
    const { setShowSentModal } = this.props;
    setShowSentModal(false);
  };

  handleConfirmationModalConfirm = () => {
    const { handleApiPromise, resendEmailProgramId } = this.props;

    handleApiPromise({
      apiPromiseFn: () => Q(resendAllInvitations(resendEmailProgramId)),
      apiTag: `${PREFIX}~${RESEND_INVITATIONS}`,
    });
  };

  handleResendInvitations = (programId: string): void => {
    const { setShowConfirmationModal, setResendEmailProgramId } = this.props;
    setShowConfirmationModal(true);
    setResendEmailProgramId(programId);
  };

  handleProgramSetupRedirect = ({
    programSlug,
    thirdPartyOrganizationSlug,
  }: {
    programSlug: string;
    thirdPartyOrganizationSlug: string;
  }): void => {
    const { router, enableNewProgramCreation } = this.context;
    const { enableGwGFreemiumMerge, enableGwGPaidMerge } = this.props;
    const enableGwGFlow = enterpriseAdminUser.isGwGOrg;
    const enableC4CBFlow = enterpriseAdminUser.isC4CBOrg;

    if (enableGwGFlow || enableGwGFreemiumMerge || enableGwGPaidMerge) {
      redirect.setLocation(
        `/o/${thirdPartyOrganizationSlug}/admin/programs/${programSlug}/${GWG_CONTINUE_PROGRAM_SETUP_PATH}`
      );
    } else if (enableC4CBFlow) {
      redirect.setLocation(
        `/o/${thirdPartyOrganizationSlug}/admin/programs/${programSlug}/${COURSERA_FOR_CAMPUS_BASIC_SETUP_PATH}`
      );
    } else if (enableNewProgramCreation) {
      router.push(`/o/${thirdPartyOrganizationSlug}/program-creation/${programSlug}/curation-options`);
    }
  };

  getInviteLearnersLink = (
    thirdPartyOrgSlug: string,
    programSlug: string,
    enableC4CBFlow?: boolean,
    enableNewProgramCreation?: boolean,
    enableGWGFlow?: boolean
  ) => {
    if (enableGWGFlow) {
      return `/o/${thirdPartyOrgSlug}/admin/programs/${programSlug}/${GWG_CONTINUE_PROGRAM_SETUP_PATH}`;
    }
    if (enableC4CBFlow) {
      return `/o/${thirdPartyOrgSlug}/admin/programs/${programSlug}/${COURSERA_FOR_CAMPUS_BASIC_SETUP_PATH}`;
    } else if (enableNewProgramCreation) {
      return `/o/${thirdPartyOrgSlug}/program-creation/${programSlug}/choose-contract`;
    } else {
      return getManageProgramLearnersLink(
        {
          thirdPartySlug: thirdPartyOrgSlug,
          programSlug,
        },
        {
          showImportLearnersModalOnMount: true,
        }
      );
    }
  };

  render() {
    const {
      programs,
      groupedProgramSummaries,
      enterpriseProgramMetrics,
      rosterRecords,
      apiStatus,
      showConfirmationModal,
      showSentModal,
      showProgramIds,
      thirdPartyOrganization,
      programIdForManagingContracts,
      setProgramIdForManagingContracts,
      resendEmailProgramId,
      enableGwGFreemiumMerge,
      enableGwGPaidMerge,
      gwgProgramIds,
    } = this.props;
    const { enableSkillsPlanningForOrg: enableSkillsPlanning, enableNewProgramCreation } = this.context;
    const totalLearnersNotJoined =
      enterpriseProgramMetrics?.getSummariesByProgramIds?.find((metrics) => metrics.id === resendEmailProgramId)
        ?.totalLearnersNotJoined || 0;

    return (
      <div>
        <Grid container direction="column" spacing={32}>
          {programs.map((program) => {
            const programSummary = groupedProgramSummaries?.[program.id]?.[0];
            const programMetrics = enterpriseProgramMetrics?.getSummariesByProgramIds?.find(
              (metrics) => metrics.id === program.id
            );
            const hasNoLearners = !programMetrics?.totalInvitationCount && !programMetrics?.totalLearnersJoined;

            const contractCount = rosterRecords?.filter(({ programId }) => programId === program.id).length;
            const tspCount = program.metadata.linkedTargetSkillProfileIds?.length || 0;
            const isGwGProgram = gwgProgramIds?.includes(program.id);

            const enableC4CBFlow = enterpriseAdminUser.isC4CBOrg;
            const enableGwGFlow = enterpriseAdminUser.isGwGOrg;

            const coreProductCard = (
              <ProgramCard
                thirdPartyOrganization={thirdPartyOrganization}
                skillSetProgramMainLink={getSkillSetProgramMainLink({
                  thirdPartySlug: thirdPartyOrganization.slug,
                  programSlug: program.slug,
                })}
                showProgramId={showProgramIds}
                programId={program.id}
                programSlug={program.slug}
                programCreatedAt={program.launchedAt}
                programName={program.name}
                numberOfCourses={programSummary?.courseCount}
                numberOfContracts={enterpriseAdminUser.isSuperUserOrEnterpriseOutsourcing() ? contractCount : undefined}
                onContractsClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  setProgramIdForManagingContracts(program.id);
                }}
                numberOfSkillSets={enableSkillsPlanning ? tspCount : undefined}
                numberOfLearnersInvited={programMetrics?.totalInvitationCount}
                inviteLearnersLink={this.getInviteLearnersLink(
                  thirdPartyOrganization.slug,
                  program.slug,
                  enableC4CBFlow,
                  enableNewProgramCreation,
                  enableGwGFlow
                )}
                numberOfLearnersJoined={programMetrics?.totalLearnersJoined}
                viewLearnersLink={getManageProgramLearnersLink({
                  thirdPartySlug: thirdPartyOrganization.slug,
                  programSlug: program.slug,
                })}
                numberOfLearnersEnrolled={programMetrics?.totalLearnersWithEnrollments}
                viewEnrolledLearnersLink={getManageProgramLearnersLink(
                  {
                    thirdPartySlug: thirdPartyOrganization.slug,
                    programSlug: program.slug,
                  },
                  {
                    presetId: 'enrolled',
                  }
                )}
                onSendReminder={this.handleResendInvitations}
                assignContentLink={getAssignContentLink(
                  {
                    thirdPartySlug: thirdPartyOrganization.slug,
                    programSlug: program.slug,
                  },
                  {
                    ref: 'program-card',
                  }
                )}
              />
            );

            return (
              <Grid item key={program.id} xs={12}>
                <TrackedReactLink
                  trackingName="program_card"
                  data={{ thirdPartyOrganizationId: thirdPartyOrganization.id }}
                  href={getProgramMainLink({
                    thirdPartySlug: thirdPartyOrganization.slug,
                    programSlug: program.slug,
                  })}
                  {...css(styles.ProgramCardLink)}
                >
                  {(enableC4CBFlow || enableGwGFlow || enableGwGFreemiumMerge || enableGwGPaidMerge) &&
                  hasNoLearners ? (
                    <EnterprisePermissionsRequired
                      domain="PROGRAM"
                      domainId={program.id}
                      subdomain={ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN.LEARNERS}
                      verb={ENTERPRISE_ADMIN_PERMISSION_VERB.MANAGE}
                      substituteComponent={coreProductCard}
                    >
                      <ProgramSetupCard
                        key={program.id}
                        thirdPartyOrganizationSlug={thirdPartyOrganization.slug}
                        showProgramId={showProgramIds}
                        programId={program.id}
                        programCreatedAt={program.launchedAt}
                        programName={program.name}
                        programSlug={program.slug}
                        onContractsClick={(event: React.MouseEvent<Element, MouseEvent>) => {
                          event.stopPropagation();
                          event.preventDefault();
                          setProgramIdForManagingContracts(program.id);
                        }}
                        rootClassName={styles.ProgramCard}
                        onContinueProgramSetup={this.handleProgramSetupRedirect}
                        isGwG={isGwGProgram}
                      />
                    </EnterprisePermissionsRequired>
                  ) : (
                    coreProductCard
                  )}
                </TrackedReactLink>
              </Grid>
            );
          })}
        </Grid>
        {programIdForManagingContracts && (
          <ProgramSummaryContractsModal
            thirdPartyOrganizationId={thirdPartyOrganization.id}
            programId={programIdForManagingContracts}
            handleClose={() => setProgramIdForManagingContracts(undefined)}
          />
        )}
        {showConfirmationModal && (
          <Modal
            className="rc-resendInvitations"
            modalName="Resend All Pending Invitations"
            handleClose={this.handleConfirmationModalCancel}
            shouldFocusOnXButton={true}
          >
            <BulkEmailConfirm
              overThresholdMessage={
                <FormattedMessage
                  message={_t('You are about to send {reminderCount} to learners who have not joined the program')}
                  reminderCount={
                    <strong>
                      {totalLearnersNotJoined} {_t('reminders')}
                    </strong>
                  }
                />
              }
              underThresholdMessage={
                <FormattedMessage
                  message={_t(
                    'You are about to send {count, plural, =1 {an invitation reminder} other {invitation reminders}} to {count} {count, plural, =1 {learner} other {learners}}'
                  )}
                  count={totalLearnersNotJoined}
                />
              }
              handleClose={this.handleConfirmationModalCancel}
              count={totalLearnersNotJoined}
              onSubmit={this.handleConfirmationModalConfirm}
              processing={apiStatus === API_IN_PROGRESS || apiStatus === API_SUCCESS}
            />
          </Modal>
        )}

        {showSentModal && (
          <Modal
            className="rc-MessageSentModal"
            modalName="Admin Messages Discard Modal"
            handleClose={this.handleDismissSentModal}
            shouldFocusOnXButton={true}
          >
            <div className="c-background vertical-box align-items-absolute-center">
              <h2 className="c-title">{_t('Your reminder has been sent')}</h2>
              <p className="c-content">
                <FormattedMessage
                  message={_t("It's on its way to {count} {count, plural, =1 {learner} other {learners}} right now")}
                  count={totalLearnersNotJoined}
                />
              </p>
            </div>
            <div className="c-button-container horizontal-box align-items-absolute-center">
              <Button variant="primary" onClick={this.handleDismissSentModal}>
                {_t('Dismiss')}
              </Button>
            </div>
          </Modal>
        )}
      </div>
    );
  }
}

const ProgramSummaryListWithHooks = (props: Props) => {
  const [programIdForManagingContracts, setProgramIdForManagingContracts] = React.useState<string | undefined>(
    undefined
  );
  const { data } = useQuery<ProgramSummaryListDetailsQuery, ProgramSummaryListDetailsQueryVariables>(
    ProgramSummaryListDetailsGraphql,
    {
      variables: {
        programIds: props.programs.map((_) => _.id),
      },
      context: { clientName: 'gatewayGql' },
    }
  );
  const enterpriseProgramMetrics = data?.EnterpriseAdminProgramMetrics;
  const [resendEmailProgramId, setResendEmailProgramId] = React.useState<string>('');
  const [showConfirmationModal, setShowConfirmationModal] = React.useState<boolean>(false);
  const [showSentModal, setShowSentModal] = React.useState<boolean>(false);
  return (
    <ProgramSummaryList
      {...props}
      enterpriseProgramMetrics={enterpriseProgramMetrics}
      programIdForManagingContracts={programIdForManagingContracts}
      setProgramIdForManagingContracts={setProgramIdForManagingContracts}
      resendEmailProgramId={resendEmailProgramId}
      setResendEmailProgramId={setResendEmailProgramId}
      showConfirmationModal={showConfirmationModal}
      setShowConfirmationModal={setShowConfirmationModal}
      showSentModal={showSentModal}
      setShowSentModal={setShowSentModal}
    />
  );
};

const enhanced = compose<Props, PropsFromCaller>(
  deferToClientSideRender(),
  Naptime.createContainer<PropsFromNaptime, PropsFromCaller>((props) => {
    const { programs } = props;
    const programIds = programs.map((program) => program.id);
    return {
      programSummaries: EnterpriseProgramSummaries.multiGet(programIds, {
        fields: ['id', 'courseCount'],
        required: false,
      }),
    };
  }),
  graphql(
    gql`
      query AdminHomeEnterpriseUserRosterQuery(
        $byProgramInput: FindByProgramIdsOrContractIdsOrBothAndIncludeDeletedRequest!
      ) {
        byPrograms(input: $byProgramInput)
          @rest(
            type: "FindByProgramIdsOrContractIdsOrBothAndIncludeDeleted"
            path: "grpc/enterpriseuser/rosters/v1/RostersAPI/FindByProgramIdsOrContractIdsOrBothAndIncludeDeleted"
            method: "POST"
          ) {
          rosterRecords
        }
      }
    `,
    {
      options: ({ programs }: PropsFromCaller) => {
        const byProgramInput: FindByProgramIdsOrContractIdsOrBothAndIncludeDeletedRequest = {
          programIds: programs.map((program) => program.id),
          contractIds: [],
        };
        return { variables: { byProgramInput }, errorPolicy: 'all' };
      },
      props: ({
        data,
      }: {
        data?: {
          byPrograms?: FindByProgramIdsOrContractIdsOrBothAndIncludeDeletedResponse;
        };
      }) => {
        return {
          rosterRecords: data?.byPrograms?.rosterRecords,
        };
      },
    }
  ),
  withProps<PropsFromWithProps, PropsFromNaptime>((props: { programSummaries: EnterpriseProgramSummaries[] }) => {
    const { programSummaries } = props;
    const hasLoadedSummaryData = !!programSummaries?.[0];
    const enableGwGFreemiumMerge = enterpriseAdminUser.isGwGFreemiumOrg;
    const enableGwGPaidMerge = enterpriseAdminUser.isGwGPaidOrg;
    return {
      groupedProgramSummaries: hasLoadedSummaryData ? groupBy(programSummaries, 'id') : {},
      enableGwGFreemiumMerge,
      enableGwGPaidMerge,
    };
  }),
  withApiHandler({
    shouldResetToDefaultStatus: true,
    apiStatusResetTime: 2000,
  })
);

export default enhanced(ProgramSummaryListWithHooks);
